// ─────────────────────────────────────────────────────────────────────────────
// Ticket enums shared by the hub and the detail page.
//
// ⚠️ THESE LIVED IN pages/DashCustomerService.js, AND AdminTicketDetail IMPORTED
// THEM FROM IT. A page importing another PAGE pulls that page's whole module
// graph into the importer's chunk and couples two routes that have no reason to
// know about each other — and if the two ever end up in a cycle, the named
// import resolves to undefined at evaluation time and `TICKET_ROLES.filter(...)`
// throws during render, which the root error boundary turns into "Something went
// wrong" with no clue where.
//
// A constants module has no such failure mode: nothing imports back into it.
//
// Mirrors backend/constants/staffRoles.js STAFF_ROLES and
// backend/constants/escalationRouting.js ESCALATION_TOPICS. The backend is the
// source of truth for what a valid role IS — the transfer endpoint validates
// against isValidStaffRole() and refuses anything else, so a stale entry here
// produces a 400 rather than a bad write.
// ─────────────────────────────────────────────────────────────────────────────
export const TICKET_TOPICS = [
  'adverse_decision', 'resale_option_choice', 'program_terms_uncertain',
  'existing_contract_termination', 'incompleteness_request', 'file_status_inactive',
  'app_ui_unconfirmed', 'app_bug', 'partner_buyer_status', 'partner_program',
  'partner_account_reactivation', 'general',
];

// ⚠️ THE THREE TEAMS A TICKET CAN BELONG TO, and the ONLY choices the transfer
// control offers. TICKET_ROLES below is the wider set of staff roles a ticket
// may be ASSIGNED to; owning a ticket is a different question with three
// answers, and the hub's tabs are exactly these. Offering account_manager or
// contract_desk in the move control produced tickets that belonged to a team
// with no tab — visible only under "All", which is where a ticket goes to be
// forgotten.
export const TICKET_TEAMS = [
  { key: 'client_success', label: 'Client Success' },
  { key: 'partner_success', label: 'Partner Success' },
  { key: 'engineering', label: 'Engineering' },
];

export const TICKET_ROLES = [
  'loan_officer', 'contract_desk', 'account_manager', 'client_success',
  'partner_success', 'engineering', 'admin',
];

// ─────────────────────────────────────────────────────────────────────────────
// THE OWNER LABEL IS AN OBJECT, NOT A STRING.
//
// ⚠️ BOTH TICKET ENDPOINTS SEND `owner_state` AS { text, tone } — it comes from
// backend/utils/ticketOwnership.js ownerLabel(), which returns a tone alongside
// the words so "No client_success on file" can be coloured as the exception it
// is. The hub read `.text`; the detail page rendered the object itself, which
// is React error #31 ("Objects are not valid as a React child") and a full-page
// crash on EVERY ticket — the "Something went wrong" screen that took three
// rounds to pin down.
//
// It survived because the two readers of one payload were written apart and
// neither named the contract. This is that contract, in the one place a page is
// allowed to import from.
// ─────────────────────────────────────────────────────────────────────────────
const OWNER_TONE_COLORS = {
  normal: '#111827',
  warn: '#c026d3',
  error: '#ff082f',
  muted: '#9ca3af',
};

export const ownerToneColor = (tone) => OWNER_TONE_COLORS[tone] || OWNER_TONE_COLORS.muted;

/** The words to show for an owner_state, never the object. */
export const ownerText = (ownerState, fallback = 'Not derived') => {
  if (!ownerState) return fallback;
  if (typeof ownerState === 'string') return ownerState;   // tolerate an older payload
  return ownerState.text || fallback;
};

// ─────────────────────────────────────────────────────────────────────────────
// WHICH TOPICS CAN APPLY TO WHOM.
//
// ⚠️ THE FULL LIST OFFERS TOPICS THAT CANNOT EXIST FOR THE PERSON IN FRONT OF
// YOU. A prospective borrower has no file, so "adverse decision", "incompleteness
// request", "file status inactive", "resale option choice" and "existing
// contract termination" are all about something they do not have — and a topic
// picked in error routes the ticket to a team that then has nothing to work
// with. A prospective partner has no buyers and no account, so the two partner
// topics about those are the same problem.
//
// ⚠️ A SUBSET, NEVER A NEW ENUM. Every value below is one the backend already
// validates and routes on — narrowing what is OFFERED is a UI decision; adding a
// topic the router has never heard of would be a silent fallthrough.
// ─────────────────────────────────────────────────────────────────────────────
const BORROWER_FILE_TOPICS = [
  'adverse_decision', 'resale_option_choice', 'existing_contract_termination',
  'incompleteness_request', 'file_status_inactive',
];
const APP_TOPICS = ['app_ui_unconfirmed', 'app_bug'];

// ⚠️ THE FIRST ENTRY IS THE DEFAULT, AND THE ORDER IS LOad-BEARING FOR PARTNERS.
// suggestRoleForTopic sends `general` to client_success for EVERY requester
// type, so a partner ticket left on the default topic lands with the borrower
// team. Each partner list therefore opens on a partner topic, which routes to
// partner_success. Re-sorting these alphabetically would silently misroute every
// partner ticket somebody did not change the topic on.
export const TOPICS_FOR = {
  borrower: {
    existing: ['general', ...BORROWER_FILE_TOPICS, 'program_terms_uncertain', ...APP_TOPICS],
    // No file yet — nothing here refers to one.
    prospective: ['general', 'program_terms_uncertain', ...APP_TOPICS],
  },
  partner: {
    existing: ['partner_buyer_status', 'partner_program', 'partner_account_reactivation', ...APP_TOPICS, 'general'],
    // No account and no buyers yet; the program itself is the live question.
    prospective: ['partner_program', 'general'],
  },
};

export const topicsFor = (who, standing) =>
  (TOPICS_FOR[who] || TOPICS_FOR.borrower)[standing] || TOPICS_FOR.borrower.existing;
